import { LinkBreakIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import {
  isPmsLookupProvider,
  PmsLookupProvider,
  PmsProvider,
  useDisconnectPms,
  useGetPmsCompanies,
  useGetPmsProperty,
} from '@/api/pms'
import { FieldRow, FieldSkeleton, FieldsTable, FieldViewValue } from '@/components/layout/field-row'
import { TabCard } from '@/components/layout/tab-card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Icon } from '@/components/ui/icon'
import { EMPTY_VALUE } from '@/constants/ui'
import { popupFactory, toastFactory } from '@/factories'
import { Building } from '@/reducers/types'
import { getApiErrorMessage } from '@/utils/error'

import { PMS_LABELS } from '../../../places.pms'

interface ConnectedPmsProps {
  building: Building
  provider: PmsProvider
}

export const ConnectedPms = ({ building, provider }: ConnectedPmsProps) => {
  const { t } = useTranslation(['organisation'])
  const { mutateAsync: disconnect, isPending } = useDisconnectPms()

  const companyId = building[`${provider}ManagementId`]
  const propertyId = building[`${provider}PropertyId`]
  const lookupProvider = isPmsLookupProvider(provider) ? (provider as PmsLookupProvider) : null

  const { data: companies, isLoading: isCompaniesLoading } = useGetPmsCompanies(lookupProvider)
  const { data: property, isLoading: isPropertyLoading } = useGetPmsProperty(
    lookupProvider,
    companyId,
    propertyId,
  )
  const companyName = companies?.find((company) => company.id === Number(companyId))?.name

  const handleDisconnect = () =>
    popupFactory.open(ConfirmationPopup, {
      children: t('organisation:places.pms.disconnectWarning'),
      confirmIcon: LinkBreakIcon,
      confirmText: t('organisation:places.integrations.pms.disconnect.button'),
      onConfirm: async () => {
        try {
          await disconnect({ buildingId: building.id, provider })
          toastFactory.success(t('organisation:places.integrations.pms.toasts.disconnected'))
        } catch (error) {
          toastFactory.error(getApiErrorMessage(error))
        }
      },
      title: t('organisation:places.integrations.pms.disconnect.confirmTitle'),
      variant: 'danger',
    })

  return (
    <TabCard
      actions={
        <Button loading={isPending} size="small" use="secondary" onClick={handleDisconnect}>
          <Icon icon={LinkBreakIcon} color="red500" />
          {t('organisation:places.integrations.pms.disconnect.button')}
        </Button>
      }
      title={t('organisation:places.integrations.pms.title')}
    >
      <FieldsTable>
        <FieldRow label={t('organisation:places.pms.connected.provider')}>
          <FieldViewValue>
            <Badge color="green" size="small" variant="light">
              {PMS_LABELS[provider]}
            </Badge>
          </FieldViewValue>
        </FieldRow>
        <FieldRow label={t('organisation:places.pms.connected.company')}>
          <FieldViewValue>
            {isCompaniesLoading ? (
              <FieldSkeleton height="1.6rem" width="20rem" />
            ) : (
              (companyName ?? String(companyId ?? EMPTY_VALUE))
            )}
          </FieldViewValue>
        </FieldRow>
        <FieldRow label={t('organisation:places.pms.connected.property')}>
          <FieldViewValue>
            {isPropertyLoading ? (
              <FieldSkeleton height="1.6rem" width="20rem" />
            ) : (
              (property?.name ?? String(propertyId ?? EMPTY_VALUE))
            )}
          </FieldViewValue>
        </FieldRow>
      </FieldsTable>
    </TabCard>
  )
}
